import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusSquare } from '@fortawesome/free-regular-svg-icons';
import { faChalkboardTeacher, faChartColumn, faChartDiagram, faDiagramPredecessor, faGauge, faHurricane, faKeyboard, faLandmark, faLayerGroup, faNetworkWired, faPlusSquare, faScrewdriverWrench, faSlidersH, faStaffSnake, faUsersRectangle, faVault, faWindowRestore, faSun, faWalkieTalkie, faFileShield, faCodeCompare, faChartGantt, faGears, faVcard, faHourglass } from '@fortawesome/free-solid-svg-icons';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const SidebarContainer = styled(motion.div)`
  width: 280px;
  height: 100vh;
  background: #ffffff;
  border-right: 1px solid var(--border-color);
  padding: .2rem .1rem 1rem;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1000;
  box-shadow: 2px 0 15px rgba(0, 0, 0, 0.2);
  transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  transition: transform 0.3s ease;
`;

const NavGroup = styled.div`
  margin-bottom: 1rem;
`;

const NavGroupTitle = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-secondary);
  margin-bottom: 0.8rem;
  padding-left: 0.1rem;
  font-weight: 600;
  opacity: 0.7;
`;

const NavItem = styled(motion.div)`
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  margin-bottom: 0.3rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: ${props => props.$active ? '#0066FF' : 'var(--text-primary)'};
  background: ${props => props.$active ? 'rgba(0, 102, 255, 0.05)' : 'transparent'};
  border-left: ${props => props.$active ? '3px solid #0066FF' : '3px solid transparent'};
  font-weight: ${props => props.$active ? '500' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.03);
    color: #0066FF;
  }
`;

const SubNavItem = styled(NavItem)`
  overflow-x: hidden;
  padding-left: .85rem;
  font-size: 0.85rem;
  margin-bottom: 0.2rem;
`;

const NavIcon = styled.div`
  width: 20px;
  height: 20px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: ${props => props.$active ? '#0066FF' : 'var(--text-secondary)'};
`;

const NavText = styled.div`
  font-size: 0.9rem;
`;

const GroupToggle = styled.div`
  margin-left: auto;
  font-size: 0.8rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: transform 0.3s ease;
  transform: ${props => props.expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const Sidebar = ({ isOpen = false, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    dashboard: true,
    agentWorkspace: false,
    agentEcosystem: false,
    knowledge: true,
    governance: false,
    system: false
  });
  
  const toggleGroup = (group) => {
    setExpandedGroups({
      ...expandedGroups,
      [group]: !expandedGroups[group]
    });
  };
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const mainNavItems = [
    { id: 'dashboard', icon: faGauge, label: 'Dashboard', path: '/dashboard' },
    { id: 'workbench', icon: faChalkboardTeacher, label: 'Workbench', path: '/listing' },
  ];
  
  const systemItems = [
    { id: 'policies', icon: faFileShield, label: 'Policies', path: '/policies' },
    { id: 'settings', icon: faGears, label: 'Settings', path: '/settings' },
    { id: 'manage', icon: faUsersRectangle, label: 'Manage', path: '/manage' },
  ];
  
  const renderNavItems = (items, isSubMenu = false) => {
    return items.map(item => (
      <Link to={item.path} key={item.id} style={{ textDecoration: 'none' }} onClick={onClose}>
        {isSubMenu ? (
          <SubNavItem
            $active={isActive(item.path)}
            onMouseEnter={() => setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
            whileHover={{ x: 5 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <NavIcon $active={isActive(item.path)}>
            {typeof item.icon === 'string' ? item.icon : <FontAwesomeIcon icon={item.icon} />}
            </NavIcon>
            <NavText>{item.label}</NavText>
          </SubNavItem>
        ) : (
          <NavItem
            $active={isActive(item.path)}
            onMouseEnter={() => setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
            whileHover={{ x: 5 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <NavIcon $active={isActive(item.path)}>
              {typeof item.icon === 'string' ? item.icon : <FontAwesomeIcon icon={item.icon} />}
            </NavIcon>
            <NavText>{item.label}</NavText>
          </NavItem>
        )}
      </Link>
    ));
  };
  
  const renderGroupWithToggle = (title, items, groupKey) => {
    return (
      <NavGroup>
        <NavItem onClick={() => toggleGroup(groupKey)} style={{justifyContent:"space-between"}}>
          <NavText style={{ color:"#9c9c9c"}}>{title}</NavText>
          <NavIcon>{expandedGroups[groupKey] ? (<FontAwesomeIcon icon={faMinusSquare} />) : (<FontAwesomeIcon icon={faPlusSquare} />)}</NavIcon>
        </NavItem>
        {expandedGroups[groupKey] && renderNavItems(items, true)}
      </NavGroup>
    );
  };
  
  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <SidebarContainer $isOpen={isOpen}>
        
        <NavGroup>
          {renderNavItems(mainNavItems)}
        </NavGroup>
        
        {renderGroupWithToggle('System', systemItems, 'system')}
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
